/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Asset, AssetSet, BlockTranscriptSegment } from "types/Asset";
import {
  Clip,
  ClipId,
  ClipWithPlaylistTimes,
  isClipTypeWithAssetSupport,
  isClipWithAsset,
  PlaceholderClip,
  RecordingSessionClip,
  TranscriptionClip,
  WaveformClip,
} from "types/Clip";
import { mapClipsToClipsWithTimes } from "utilities/ClipUtilities";
import { BlockTypes } from "constants/BlockTypes";
import {
  Block,
  BlockParagraph,
  BlockToken,
  blockTokens,
  isTranscriptionBlock,
  PlaceholderBlock,
  RecordingBlock,
  TranscriptionBlock,
  WaveformBlock,
  WaveformUploadInfo,
} from "types/Block";
import { generateClipTranscript } from "utilities/TranscriptUtilities";
import { ClipUploadStates } from "constants/ClipUploadStates";
import { AudioBufferState } from "constants/AudioBufferState";
import { createSelector } from "reselect";
import { AppState } from "types/AppState";
import {
  BlockFinalizer,
  BlockIdGenerator,
  BlockIdStabilizer,
} from "utilities/BlockIDs";

export const getBlocksSelector = createSelector(
  (state: AppState) => state.clips,
  (state) => state.assets,
  (clips, assets) => generateBlocksFromClips(clips, assets)
);

const getWaveformUploadInfo = (clip: WaveformClip): WaveformUploadInfo => {
  return {
    uploadState: clip.uploadState,
    name: clip.name,
  };
};

export function generateBlocksFromClips(
  clips: Clip[],
  assets: AssetSet,
  blockIdGenerator: BlockIdStabilizer = BlockIdStabilizer.instance
): Block[] {
  const clipsWithTimes = mapClipsToClipsWithTimes(clips);

  const segmentGrouping = groupClipsSegments(clipsWithTimes, assets);

  return blockIdGenerator.withGenerator((generator) => {
    return segmentGrouping.map(
      (segmentGrouping): Block | BlockFinalizer<TranscriptionBlock> => {
        if (segmentGrouping.type === "block") {
          const { clip, asset } = segmentGrouping;
          switch (clip.type) {
            case BlockTypes.RECORDING_SESSION:
              return buildRecordingBlock(clip, asset);
            case BlockTypes.PLACEHOLDER:
              return buildPlaceholderBlock(clip);
            case BlockTypes.WAVEFORM:
              return buildWaveformBlock(clip, asset);
          }
        } else {
          return buildTranscriptBlock(segmentGrouping, generator);
        }
      }
    );
  });
}

type SegmentGrouping = SingleClipGroup | TranscriptGroup;

interface SingleClipGroup {
  type: "block";
  clip: ClipWithPlaylistTimes &
    (WaveformClip | PlaceholderClip | RecordingSessionClip);
  asset: Asset | null;
}

interface TranscriptGroup {
  type: "transcript";
  assetIds: string[];
  clipIds: ClipId[];
  segments: BlockTranscriptSegment[];
  uploadState: ClipUploadStates;
  audioBufferState: AudioBufferState;
}

const groupClipsSegments = (
  clipsWithTimes: ClipWithPlaylistTimes[],
  assets: AssetSet
): SegmentGrouping[] => {
  const groupedClips: SegmentGrouping[] = [];
  clipsWithTimes.forEach((clip) => {
    const asset =
      isClipTypeWithAssetSupport(clip) && clip.assetId
        ? assets[clip.assetId]
        : null;

    if (clip.type === BlockTypes.TRANSCRIPTION) {
      insertOrAppendTranscriptionClip(clip, asset, groupedClips);
    } else {
      groupedClips.push({
        type: "block",
        clip,
        asset,
      });
    }
  });

  return groupedClips;
};

const insertOrAppendTranscriptionClip = (
  clip: ClipWithPlaylistTimes & TranscriptionClip,
  asset: Asset | null,
  groupedClips: SegmentGrouping[]
): void => {
  const clipTranscriptSegments = generateClipTranscript(clip, asset);
  clipTranscriptSegments.forEach((segment) => {
    const prevGroup: SegmentGrouping | undefined =
      groupedClips[groupedClips.length - 1];

    if (isPartOfGroup(prevGroup, clip, segment)) {
      if (!prevGroup.clipIds.includes(clip.id)) {
        prevGroup.clipIds.push(clip.id);
      }

      if (clip.assetId && !prevGroup.assetIds.includes(clip.assetId)) {
        prevGroup.assetIds.push(clip.assetId);
      }
      prevGroup.segments.push(segment);
    } else {
      if (!isClipWithAsset(clip)) {
        console.warn("TranscriptionClip has no assetId", clip);
        return;
      }

      groupedClips.push(buildTranscriptGroup(clip, segment, asset));
    }
  });
};

const isPartOfGroup = (
  prevGroup: SegmentGrouping,
  clip: ClipWithPlaylistTimes & TranscriptionClip,
  segment: BlockTranscriptSegment
): prevGroup is TranscriptGroup => {
  return (
    prevGroup &&
    prevGroup.type === "transcript" &&
    (segmentsMatchByAssetSpeaker(prevGroup, clip, segment) ||
      segmentsMatchByAdobeId(prevGroup.segments[0], segment) ||
      segmentsMatchByLegacyId(prevGroup.segments[0], segment))
  );
};

const segmentsMatchByAssetSpeaker = (
  prevGroup: TranscriptGroup,
  clip: ClipWithPlaylistTimes & TranscriptionClip,
  otherSegment: BlockTranscriptSegment
): boolean => {
  return Boolean(
    clip.assetId &&
      prevGroup.assetIds.includes(clip.assetId) &&
      prevGroup.segments[0].unidentifiedSpeakerNumber ===
        otherSegment.unidentifiedSpeakerNumber
  );
};

const segmentsMatchByAdobeId = (
  segment: BlockTranscriptSegment,
  otherSegment: BlockTranscriptSegment
): boolean => {
  return Boolean(
    segment.adobeId &&
      otherSegment.adobeId &&
      segment.adobeId === otherSegment.adobeId
  );
};

const segmentsMatchByLegacyId = (
  segment: BlockTranscriptSegment,
  otherSegment: BlockTranscriptSegment
): boolean => {
  return Boolean(
    segment.userData &&
      otherSegment.userData &&
      segment.userData.id === otherSegment.userData.id
  );
};

const buildTranscriptGroup = (
  clip: ClipWithPlaylistTimes & TranscriptionClip,
  segment: BlockTranscriptSegment,
  asset: Asset | null
): TranscriptGroup => {
  return {
    type: "transcript",
    clipIds: [clip.id],
    assetIds: [clip.assetId!],
    segments: [segment],
    uploadState: clip.uploadState,
    audioBufferState: asset?.audioBufferState ?? AudioBufferState.PENDING,
  };
};

const buildRecordingBlock = (
  clip: ClipWithPlaylistTimes & RecordingSessionClip,
  asset: Asset | null
): RecordingBlock => ({
  id: clip.id,
  type: BlockTypes.RECORDING_SESSION,
  uploadState: clip.uploadState,
  duration: clip.duration,

  // TODO: Determine if these are actually needed and remove from type?
  startTime: clip.startTime,
  endTime: clip.endTime,
  clipIds: [],

  transcriptionState: asset?.transcriptionState,
});

const buildPlaceholderBlock = (
  clip: ClipWithPlaylistTimes & PlaceholderClip
): PlaceholderBlock => ({
  id: clip.id,
  clipIds: [clip.id],
  type: BlockTypes.PLACEHOLDER,
  startTime: clip.startTime,
  endTime: clip.endTime,
  // duration: clip.duration,
  text: clip.text,
  uploadState: null,
});

const buildWaveformBlock = (
  clip: ClipWithPlaylistTimes & WaveformClip,
  asset: Asset | null
): WaveformBlock => ({
  ...getWaveformUploadInfo(clip),
  id: clip.id,
  clipIds: [clip.id], // Can this be multiple clips?
  type: BlockTypes.WAVEFORM,
  assetId: clip.assetId,
  offset: clip.offset,
  duration: clip.duration,
  isBackground: clip.isBackground,
  startTime: clip.startTime,
  endTime: clip.endTime,
  fadeOut: clip.fadeOut,
  fadeIn: clip.fadeIn,
  transcriptionState: asset?.transcriptionState || null,
  audioBufferState: asset?.audioBufferState ?? AudioBufferState.PENDING,
});

const buildTranscriptBlock = (
  segmentGrouping: TranscriptGroup,
  blockIdGenerator: BlockIdGenerator
): BlockFinalizer<TranscriptionBlock> => {
  const firstSegment = segmentGrouping.segments[0];
  const tokens = concatTranscriptionSegments(segmentGrouping.segments);
  tokens[0].id = `start-of-block`;
  const paragraphs = splitTokensIntoParagraphs(tokens);

  return blockIdGenerator.requestBlockId(
    segmentGrouping.clipIds[0],
    segmentGrouping.segments[0].segmentIndex,
    tokens.length,
    (blockId) => {
      return {
        id: blockId,
        type: BlockTypes.TRANSCRIPTION,
        clipIds: segmentGrouping.clipIds,
        transcript: paragraphs,
        startTime: tokens[0].playlistStartTime,
        endTime: tokens[tokens.length - 1].playlistEndTime,
        assetIds: segmentGrouping.assetIds,
        unidentifiedSpeakerNumber:
          segmentGrouping.segments[0].unidentifiedSpeakerNumber,
        adobeId: firstSegment.adobeId,
        displayName: firstSegment.displayName,
        displayNameIsOverridden: firstSegment.displayNameIsOverridden,
        isAnonymous: firstSegment.isAnonymous,
        userData: firstSegment.userData,
        uploadState: segmentGrouping.uploadState,
        audioBufferState: segmentGrouping.audioBufferState,
        _flatTokens: tokens,
      };
    }
  );
};

const splitTokensIntoParagraphs = (tokens: BlockToken[]): BlockParagraph[] => {
  const paragraphs: BlockParagraph[] = [];

  tokens.forEach((token, tokenIndex) => {
    const currentParagraph: BlockParagraph | undefined =
      paragraphs[paragraphs.length - 1];
    // Super naive way to say "a paragraph should have x number of sentences" in order to create sensible visual "blocks"
    const sentenceEndingsInCurrentParagraph =
      currentParagraph?.tokens.filter((token) => token.text?.match(/[.!?]$/g))
        .length ?? 0;
    const prevToken: BlockToken | undefined =
      currentParagraph?.tokens?.[currentParagraph.tokens.length - 1];
    if (
      currentParagraph &&
      prevToken &&
      shouldAddToParagraph(
        prevToken,
        sentenceEndingsInCurrentParagraph,
        tokenIndex
      )
    ) {
      currentParagraph.tokens.push(token);
    } else {
      paragraphs.push({
        tokens: [token],
        indexOffset: tokenIndex,
      });
    }
  });

  return paragraphs;
};

const NEW_PARAGRAPH_THRESHOLD_SENTENCES = 4;

const shouldAddToParagraph = (
  prevToken: BlockToken,
  sentenceEndingsInCurrentParagraph: number,
  tokenIndex: number
): boolean => {
  return (
    sentenceEndingsInCurrentParagraph < NEW_PARAGRAPH_THRESHOLD_SENTENCES ||
    prevToken.text !== null ||
    tokenIndex === 1 // initial spaces shouldn't form their own paragraphs
  );
};

// concatenate a list of transcription segments (each containing multiple tokens) into
// a single list of tokens, merging spacer tokens at the boundary of each segment.
const concatTranscriptionSegments = (
  segments: BlockTranscriptSegment[]
): BlockToken[] => {
  const tokens: BlockToken[] = [];
  segments.forEach((transcriptSegment) => {
    const lastToken = tokens[tokens.length - 1];
    if (!lastToken) {
      tokens.push(...transcriptSegment.tokens);
      return;
    }

    const nextToken = transcriptSegment.tokens[0];

    if (nextToken.text) {
      lastToken.playlistEndTime = nextToken.playlistStartTime;
      tokens.push(...transcriptSegment.tokens);
    } else {
      lastToken.playlistEndTime = nextToken.playlistEndTime;
      tokens.push(...transcriptSegment.tokens.slice(1));
    }
  });
  return tokens;
};

export function isTranscriptEmpty(state: AppState): boolean {
  const blocks = getBlocksSelector(state).filter(isTranscriptionBlock);
  return blocks.length === 0;
}

export function generateTranscriptPreview(state: AppState): string {
  const blocks = getBlocksSelector(state).filter(isTranscriptionBlock);
  const targetTokens = 80;
  const tokens = [];
  for (let block of blocks) {
    tokens.push(...blockTokens(block).slice(0, targetTokens - tokens.length));
    if (tokens.length >= targetTokens) {
      break;
    }
  }
  return tokens
    .filter((token) => token.text)
    .map((token) => token.text)
    .join(" ");
}
